/**
 * benchmarkService — always-on, auto-logging telemetry for Ably vs REST video-sync.
 *
 * HOW IT WORKS
 * ─────────────
 * 1. startSession()  — called by Analyse.js when a match loads.
 *    Resets in-memory counters, kicks off a 60-second auto-save interval,
 *    and registers a beforeunload handler so the final snapshot is never lost.
 *
 * 2. record*()       — called by VideoTabOld / InputBoard / ablyManager on
 *    every API call / Ably message / event submission. Counters stay in memory
 *    (fast, zero I/O on the hot path).
 *
 * 3. Auto-save       — every 60 s the live summary is written to localStorage
 *    key "sp_sessions" (a ring buffer of up to MAX_SESSIONS entries).
 *    This is data storage, not a flag — it works on mobile and desktop alike.
 *
 * 4. endSession()    — called automatically on beforeunload / visibilitychange.
 *    Writes the final snapshot and clears the auto-save interval.
 *
 * 5. getComparison() — given a matchId, finds the most-recent Ably session and
 *    most-recent REST session in the stored history and returns them side-by-side
 *    with computed diff percentages. No analyst action required.
 *
 * 6. downloadJSON()         — exports the current live session.
 *    downloadComparisonJSON() — exports the Ably vs REST comparison for this match.
 *    Both trigger a device file download (works on iOS/Android browsers).
 */

// ─── Feature flag ─────────────────────────────────────────────────────────────
// Set to true to re-enable collection and the FAB button.
const BENCHMARK_ENABLED = false;

// ─── Storage ──────────────────────────────────────────────────────────────────

const SESSIONS_KEY  = 'sp_sessions';   // localStorage key for persisted sessions
const MAX_SESSIONS  = 40;              // keep last 40 completed sessions
const AUTO_SAVE_MS  = 60_000;          // flush live counters to storage every 60 s

// ─── Service class ────────────────────────────────────────────────────────────

class BenchmarkService {
  constructor() {
    // Session identity
    this._sessionId    = null;
    this._matchId      = null;
    this._analystId    = null;
    this._syncMode     = null;   // 'ably' | 'rest'
    this._sessionStart = null;

    // Counters — incremented on every event, never synchronously written to storage
    this._restPushCount     = 0;
    this._ablyPublishCount  = 0;
    this._restFetchCount    = 0;
    this._staleWarnings     = 0;
    this._reconnects        = 0;
    this._totalEventsTagged = 0;

    // Ably round-trip latency samples (publish → receive, ms)
    this._latencies = [];

    // Event samples: one entry per tagging action
    this._eventSamples = []; // [{ at, videoTimeUsed, syncMode }]

    // Auto-save
    this._autoSaveTimer    = null;
    this._sessionEnded     = false;

    // Register once — auto-end on page leave
    this._boundEnd = () => this.endSession();
    window.addEventListener('beforeunload',      this._boundEnd);
    window.addEventListener('visibilitychange',  () => {
      if (document.visibilityState === 'hidden') this._flush();
    });
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  /**
   * Call when a match loads (Analyse.js useEffect).
   * Always-on — no flags, no opt-in.
   */
  startSession({ matchId, analystId, syncMode }) {
    if (!BENCHMARK_ENABLED) return;
    // End any previous session before starting a new one
    if (this._sessionId) this.endSession();

    this._sessionId         = _uuid();
    this._matchId           = String(matchId  ?? 'unknown');
    this._analystId         = String(analystId ?? 'unknown');
    this._syncMode          = syncMode ? 'ably' : 'rest';
    this._sessionStart      = new Date().toISOString();
    this._sessionEnded      = false;

    // Reset counters
    this._restPushCount     = 0;
    this._ablyPublishCount  = 0;
    this._restFetchCount    = 0;
    this._staleWarnings     = 0;
    this._reconnects        = 0;
    this._totalEventsTagged = 0;
    this._latencies         = [];
    this._eventSamples      = [];

    // Write an initial entry so the session is visible even if the analyst
    // closes before the first 60-s auto-save fires.
    this._flush();

    // Start periodic auto-save
    this._autoSaveTimer = setInterval(() => this._flush(), AUTO_SAVE_MS);

    console.debug('[Benchmark] Session started', {
      sessionId: this._sessionId,
      matchId: this._matchId,
      syncMode: this._syncMode,
    });
  }

  /**
   * Finalises the session — writes a complete snapshot and stops auto-save.
   * Called automatically on beforeunload / visibilitychange=hidden.
   * Safe to call multiple times.
   */
  endSession() {
    if (!this.isActive || this._sessionEnded) return;
    this._sessionEnded = true;

    clearInterval(this._autoSaveTimer);
    this._autoSaveTimer = null;

    this._persistSession({ ...this.getSummary(), session_end: new Date().toISOString() });
    console.debug('[Benchmark] Session ended', this.getSummary());
  }

  /** true once startSession() has been called for this page load */
  get isActive() {
    return this._sessionId !== null;
  }

  // ─── Recording ──────────────────────────────────────────────────────────────

  /** VideoTabOld REST push path (setInterval every 1 s) */
  recordRestPush(timestampS) {
    if (!this.isActive) return;
    this._restPushCount++;
  }

  /**
   * VideoTabOld Ably publish path.
   * Returns a wallclock stamp embedded in the Ably message so InputBoard
   * can measure the end-to-end publish→receive latency.
   * @returns {number|null} publishedAt epoch ms
   */
  recordAblyPublish(timestampS) {
    if (!this.isActive) return null;
    this._ablyPublishCount++;
    return Date.now();
  }

  /** InputBoard Ably receive path */
  recordAblyReceive(timestampS, publishedAt) {
    if (!this.isActive) return;
    if (publishedAt != null) {
      this._latencies.push(Date.now() - publishedAt);
    }
  }

  /** InputBoard REST fetch (get_match_video_data/v2/) */
  recordRestFetch(timestampS) {
    if (!this.isActive) return;
    this._restFetchCount++;
  }

  /** InputBoard — called just before an event string is written */
  recordEventSubmit({ videoTimeUsed, syncMode }) {
    if (!this.isActive) return;
    this._totalEventsTagged++;
    this._eventSamples.push({
      at: new Date().toISOString(),
      videoTimeUsed,
      syncMode,
    });
  }

  /** InputBoard stale-sync watchdog */
  recordStaleWarning() {
    if (!this.isActive) return;
    this._staleWarnings++;
  }

  /** Ably connection resumed after drop */
  recordAblyReconnect() {
    if (!this.isActive) return;
    this._reconnects++;
  }

  // ─── Read ───────────────────────────────────────────────────────────────────

  /** Full summary of the current live session */
  getSummary() {
    const latSorted = [...this._latencies].sort((a, b) => a - b);
    const avgLat = latSorted.length
      ? Math.round(latSorted.reduce((s, v) => s + v, 0) / latSorted.length)
      : null;
    const p95Lat = latSorted.length
      ? latSorted[Math.floor(latSorted.length * 0.95)]
      : null;

    return {
      session_id:           this._sessionId,
      match_id:             this._matchId,
      analyst_id:           this._analystId,
      sync_mode:            this._syncMode,
      session_start:        this._sessionStart,
      exported_at:          new Date().toISOString(),
      rest_push_count:      this._restPushCount,
      ably_publish_count:   this._ablyPublishCount,
      rest_fetch_count:     this._restFetchCount,
      stale_warnings:       this._staleWarnings,
      reconnects:           this._reconnects,
      total_events_tagged:  this._totalEventsTagged,
      avg_ably_latency_ms:  avgLat,
      p95_ably_latency_ms:  p95Lat,
      event_samples:        this._eventSamples,
    };
  }

  /** Lightweight read for the FAB button — no array operations */
  getLiveStats() {
    return {
      isActive:           this.isActive,
      syncMode:           this._syncMode,
      matchId:            this._matchId,
      restPushCount:      this._restPushCount,
      ablyPublishCount:   this._ablyPublishCount,
      restFetchCount:     this._restFetchCount,
      staleWarnings:      this._staleWarnings,
      reconnects:         this._reconnects,
      totalEventsTagged:  this._totalEventsTagged,
      ablyLatencySamples: this._latencies.length,
    };
  }

  // ─── History & comparison ────────────────────────────────────────────────────

  /**
   * Returns all persisted sessions from localStorage (newest-first).
   * Each entry is a full getSummary() snapshot with `session_end`.
   */
  getStoredSessions() {
    return _readSessions().slice().reverse(); // newest-first
  }

  /**
   * Returns a side-by-side comparison of the most-recent Ably session and
   * most-recent REST session for the given matchId.
   *
   * Returns null if fewer than two sessions (of different modes) exist.
   *
   * @param {string} matchId
   * @returns {{ ably, rest, diff } | null}
   */
  getComparison(matchId) {
    const all = _readSessions().filter((s) => String(s.match_id) === String(matchId));

    // newest-first within each mode
    const byMode = (mode) =>
      all.filter((s) => s.sync_mode === mode).sort((a, b) =>
        new Date(b.session_start) - new Date(a.session_start)
      );

    const ablySessions = byMode('ably');
    const restSessions = byMode('rest');

    if (!ablySessions.length || !restSessions.length) return null;

    const ably = ablySessions[0];
    const rest = restSessions[0];

    const pct = (before, after) =>
      before > 0 ? Math.round(((before - after) / before) * 100) : null;

    return {
      match_id:      matchId,
      compared_at:   new Date().toISOString(),
      ably,
      rest,
      diff: {
        rest_push_reduction_pct:  pct(rest.rest_push_count,   ably.rest_push_count),
        rest_fetch_reduction_pct: pct(rest.rest_fetch_count,  ably.rest_fetch_count),
        stale_warnings_ably:      ably.stale_warnings,
        reconnects_ably:          ably.reconnects,
        avg_ably_latency_ms:      ably.avg_ably_latency_ms,
        p95_ably_latency_ms:      ably.p95_ably_latency_ms,
        events_tagged_ably:       ably.total_events_tagged,
        events_tagged_rest:       rest.total_events_tagged,
      },
    };
  }

  // ─── Downloads ──────────────────────────────────────────────────────────────

  /** Exports the current live session as a JSON file */
  downloadJSON() {
    if (!this.isActive) return;
    _triggerDownload(
      this.getSummary(),
      `benchmark_match${this._matchId}_${this._syncMode}_${Date.now()}.json`
    );
  }

  /**
   * Exports the Ably vs REST comparison for the current match as a JSON file.
   * If only one mode has been recorded yet, falls back to downloading the
   * current session so the analyst always gets something useful.
   */
  downloadComparisonJSON() {
    if (!this.isActive) return;
    const comparison = this.getComparison(this._matchId);
    if (comparison) {
      _triggerDownload(
        comparison,
        `comparison_match${this._matchId}_${Date.now()}.json`
      );
    } else {
      // Only one mode recorded so far — download current session instead
      this.downloadJSON();
    }
  }

  // ─── Internal ───────────────────────────────────────────────────────────────

  /** Write/update the current session in the persisted ring buffer */
  _flush() {
    if (!this.isActive) return;
    this._persistSession(this.getSummary());
  }

  /**
   * Upserts a session record into the localStorage ring buffer.
   * Matches on session_id — updates in-place if found, otherwise appends.
   * Trims oldest entries once the buffer exceeds MAX_SESSIONS.
   */
  _persistSession(sessionData) {
    try {
      const sessions = _readSessions();
      const idx = sessions.findIndex((s) => s.session_id === sessionData.session_id);

      if (idx >= 0) {
        sessions[idx] = sessionData;           // update existing snapshot
      } else {
        sessions.push(sessionData);            // new session
        if (sessions.length > MAX_SESSIONS) {
          sessions.splice(0, sessions.length - MAX_SESSIONS); // drop oldest
        }
      }

      localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
    } catch {
      // Silently ignore quota errors — in-memory data is still intact
    }
  }
}

// ─── Module-level helpers ─────────────────────────────────────────────────────

function _readSessions() {
  try {
    const raw = localStorage.getItem(SESSIONS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function _triggerDownload(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function _uuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ─── Singleton ────────────────────────────────────────────────────────────────

export const benchmarkService = new BenchmarkService();
